"use strict";
/**
 * Contains utilities to print a BibleRef as a string
 */
Object.defineProperty(exports, "__esModule", { value: true });
const range_manip_1 = require("./range-manip");
;
const DEFAULT_OPTS = {
    use_book_id: false,
    verse_seperator: ':',
    strip_whitespace: false,
    compact: false,
    combine_ranges: false,
    lowercase: false,
    ref_seperator: '; ',
    url: false,
};
const DEFAULT_OPTS_URL = {
    use_book_id: true,
    verse_seperator: 'v',
    strip_whitespace: true,
    compact: true,
    combine_ranges: false,
    ref_seperator: '_',
    lowercase: true,
    url: true,
};
// Strips whitespace if option is set
function _stripWhitespaceMaybe(opts, str) {
    return opts.strip_whitespace ? str.replace(/ /g, '') : str;
}
// Returns a string representing the name of the specified book id
// obeying the format options
// Will have a trailing space unless strip_whitespace is set
function _formatBookName(v, id, opts) {
    let out = opts.use_book_id ? id : v.book[id].name;
    return opts.lowercase ? out.toLowerCase() : out;
}
// Formats a chapter verse specifier, eg "3:5"
function _formatChapterVerse(x, opts) {
    return `${x.chapter}${opts.verse_seperator}${x.verse}`;
}
function _generateFormatOpts(arg_opts) {
    if (arg_opts === undefined) {
        return DEFAULT_OPTS;
    }
    if (arg_opts.url) {
        return Object.assign(Object.assign({}, DEFAULT_OPTS_URL), arg_opts);
    }
    else {
        return Object.assign(Object.assign({}, DEFAULT_OPTS), arg_opts);
    }
}
/**
 * Format a single BibleVerse to a string
 * @private
 */
function formatBibleVerse(v, x, arg_opts) {
    let opts = _generateFormatOpts(arg_opts);
    return _stripWhitespaceMaybe(opts, _formatBookName(v, x.book, opts) + ' ' + _formatChapterVerse(x, opts));
}
exports.formatBibleVerse = formatBibleVerse;
/**
 * Format a single BibleRange to a string
 * @private
 */
function formatBibleRange(v, x, arg_opts) {
    let opts = _generateFormatOpts(arg_opts);
    let b_meta = v.book[x.start.book];
    if (x.start.book !== x.end.book) {
        // cross book range
        // Format as two completely seperate BibleVerse refs, joined by " - "
        return _stripWhitespaceMaybe(opts, formatBibleVerse(v, x.start, opts) +
            ' - ' +
            formatBibleVerse(v, x.end, opts));
    }
    else if (x.start.chapter !== x.end.chapter) {
        // Cross chapter range within single book
        if (opts.compact &&
            x.start.chapter === 1 &&
            x.start.verse === 1 &&
            x.end.verse === b_meta.chapters[x.end.chapter - 1].verse_count) {
            // then its a range of complete chapters
            if (x.end.chapter === b_meta.chapters.length) {
                // Its a complete book, format as "Genesis"
                return _formatBookName(v, x.start.book, opts);
            }
            else {
                // format as "Geneses 1 - 2"
                return _stripWhitespaceMaybe(opts, _formatBookName(v, x.start.book, opts) +
                    ` ${x.start.chapter} - ${x.end.chapter}`);
            }
        }
        // Format as "Genesis 1:2 - 3:4"
        return _stripWhitespaceMaybe(opts, _formatBookName(v, x.start.book, opts) + ' ' +
            _formatChapterVerse(x.start, opts) + ' - ' +
            _formatChapterVerse(x.end, opts));
    }
    else if (x.start.verse !== x.end.verse) {
        if (opts.compact &&
            x.start.verse === 1 &&
            x.end.verse === b_meta.chapters[x.end.chapter - 1].verse_count) {
            // then its an entire chapter, format as "Genesis 1"
            return _stripWhitespaceMaybe(opts, _formatBookName(v, x.start.book, opts) + ' ' + x.start.chapter);
        }
        // Range of verses within single chapter
        // Format as "Genesis 1:2-3"
        return formatBibleVerse(v, x.start, opts) + '-' + x.end.verse;
    }
    else {
        // Then start == end
        return formatBibleVerse(v, x.start, opts);
    }
}
exports.formatBibleRange = formatBibleRange;
/**
 * Format a list of BibleRefs to strings using commas to shorten the output
 * wherever possible
 * Eg, mapping formatBibleVerse over [
 *   { book: 'GEN', chapter: 1, verse: 1 },
 *   { book: 'GEN', chapter: 1, verse: 2 },
 * ]
 * would produce "Genesis 1:1" and "Genesis 1:2"
 * By using this function we instead get the more compact "Genesis 1:1,2"
 * @private
 */
function formatBibleRefList(v, xs, arg_opts) {
    if (xs.length == 0) {
        return "";
    }
    let opts = _generateFormatOpts(arg_opts);
    if (opts.combine_ranges) {
        xs = range_manip_1.combineRanges.bind({ versification: v })(xs);
    }
    let cur_book = null;
    let cur_chpt = null;
    let results = [];
    let cur_str = "";
    let cv_seperator = ", ";
    function makeNewRef(x) {
        if (cur_str.length > 0) {
            results.push(cur_str);
        }
        ;
        cur_str = "";
        if (x['is_range']) {
            cur_str = formatBibleRange(v, x, opts);
            if (x.start.book === x.end.book) {
                cur_book = x.start.book;
            }
            if (x.start.chapter === x.end.chapter) {
                cur_chpt = x.start.chapter;
            }
        }
        else {
            cur_str = formatBibleVerse(v, x, opts);
            cur_book = x.book;
            cur_chpt = x.chapter;
        }
    }
    for (let x of xs) {
        if (!x.is_range) { // Then we're dealing with a single verse
            if (x.book != cur_book) {
                // Nothing to reuse
                makeNewRef(x);
                continue;
            }
            if (x.chapter != cur_chpt) {
                // Can reuse book only
                cur_str += cv_seperator + _formatChapterVerse(x, opts);
                cur_chpt = x.chapter;
                continue;
            }
            // Can reuse book and chapter
            cur_str += "," + x.verse;
            continue;
        }
        // If still going then we're dealing with a range
        if (x.start.book !== x.end.book || x.start.book !== cur_book) {
            // Don't reuse anything if its a cross book range, or
            // if the range is within a single book - but not the current book
            makeNewRef(x);
            continue;
        }
        // If still going then we can reuse the book part...
        if (x.start.chapter !== x.end.chapter || x.start.chapter !== cur_chpt) {
            // Cannot reuse chapter part
            cur_str += (cv_seperator +
                _formatChapterVerse(x.start, opts) + ' - ' +
                _formatChapterVerse(x.end, opts));
            continue;
        }
        // Can reuse chapter part
        cur_str += "," + x.start.verse + "-" + x.end.verse;
    }
    results.push(cur_str);
    return _stripWhitespaceMaybe(opts, results.join(opts.ref_seperator || '; '));
}
exports.formatBibleRefList = formatBibleRefList;
